import styled from '@emotion/styled';
import { BrandLogo, Button, IconButton, Text } from '@template-project/afrodita-components';
import { Link } from 'react-router-dom';
import { FocusTrapWrapper } from '@/components/FocusTrapWrapper';

/* ------------------------------------------------------------------ */
/*  Header bar                                                         */
/* ------------------------------------------------------------------ */

export const HeaderContainer = styled.header`
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space['2xl']};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  }
`;

export const Logo = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
`;

export const LogoText = styled(Text)`
  letter-spacing: -0.02em;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: ${({ theme }) => theme.component?.text?.variants?.heading5?.fontSize ?? '1.25rem'};
  }
`;

export const HeaderBrandLogo = styled(BrandLogo)`
  max-width: 204px;
  max-height: 35px;

  img,
  svg {
    max-width: 204px;
    max-height: 35px;
  }

  span {
    font-size: ${({ theme }) => theme.component?.text?.variants?.heading3?.fontSize ?? '1.875rem'};
    letter-spacing: -0.02em;
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    max-width: 160px;

    img,
    svg {
      max-width: 160px;
    }

    span {
      font-size: ${({ theme }) => theme.component?.text?.variants?.heading5?.fontSize ?? '1.25rem'};
    }
  }
`;

/* ------------------------------------------------------------------ */
/*  Desktop nav                                                        */
/* ------------------------------------------------------------------ */

export const DesktopNav = styled.nav`
  display: flex;
  gap: ${({ theme }) => theme.space.xl};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: none;
  }
`;

export const NavLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  transition: color 0.2s;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const SaleLink = styled(NavLink)`
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.urgent.color};
    opacity: 0.8;
  }
`;

/* ------------------------------------------------------------------ */
/*  Mobile header layout                                               */
/* ------------------------------------------------------------------ */

export const MobileLeft = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: flex;
    align-items: center;
  }
`;

export const MobileRight = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.space.xs};
  }
`;

/* ------------------------------------------------------------------ */
/*  Shared buttons                                                     */
/* ------------------------------------------------------------------ */

export const HeaderIconButton = styled(IconButton)`
  position: relative;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/* ------------------------------------------------------------------ */
/*  Mobile drawer                                                      */
/* ------------------------------------------------------------------ */

/** Flex chain: DrawerContent → trap → layout → scroll area */
export const MobileDrawerFocusTrap = styled(FocusTrapWrapper)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

export const DrawerLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

export const DrawerCloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: calc(${({ theme }) => theme.space.sm} + env(safe-area-inset-top, 0px)) ${({ theme }) => theme.space.lg} ${({ theme }) => theme.space.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  flex-shrink: 0;
`;

export const DrawerScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding-bottom: calc(${({ theme }) => theme.space['2xl']} + env(safe-area-inset-bottom, 0px));
`;

export const DrawerSection = styled.div`
  padding: ${({ theme }) => theme.space.lg};
  padding-top: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
`;

export const SearchShortcut = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  width: 100%;
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.background.disabled};
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

export const DrawerNav = styled.nav`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.space.lg};
  gap: ${({ theme }) => theme.space.md};
`;

export const DrawerNavLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  padding: ${({ theme }) => theme.space.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const DrawerSaleLink = styled(DrawerNavLink)`
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.urgent.color};
    opacity: 0.8;
  }
`;

export const DrawerAccountButton = styled(Button)`
  width: 100%;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.md} 0;
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  min-width: auto;
  min-height: auto;
`;
